/**
 * useHarvestMonitoringForeman Hook
 * Custom hook for managing the per-foreman-group monitoring data and state.
 */

import { useState, useCallback } from 'react';
import { HarvestForemanApiService } from '../services/harvestForemanApiService';
import type { HarvestForemanRecord, HarvestMonitoringFilters } from '../types';
import dayjs from 'dayjs';

export const useHarvestMonitoringForeman = () => {
    const [data, setData] = useState<HarvestForemanRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Initialize with today's date
    const [filters, setFilters] = useState<HarvestMonitoringFilters>({
        current_date: dayjs().format('YYYYMMDD'),
    });

    const fetchData = useCallback(async (newFilters?: Partial<HarvestMonitoringFilters>) => {
        setLoading(true);
        setError(null);

        try {
            const appliedFilters = newFilters ? { ...filters, ...newFilters } : filters;
            const response = await HarvestForemanApiService.getMonitoringForeman(appliedFilters);

            if (response.status === 'success') {
                setData(response.data || []);
                if (newFilters) {
                    setFilters(appliedFilters);
                }
            } else {
                throw new Error(response.message || 'Failed to fetch data');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch harvest monitoring data';
            setError(errorMessage);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    return {
        data,
        loading,
        error,
        filters,
        fetchData,
    };
};
